import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAnalysis } from 'api/historyApi';
import { ApiError } from 'api/restClient';
import { engineLabel } from 'api/analysisApi';
import { AnalysisView } from 'components/analysis/AnalysisView';
import { AnalysisTrace } from 'components/analysis/AnalysisTrace';
import { BacktestPanel } from './BacktestPanel';
import { ForkPanel } from './ForkPanel';
import { DEFAULT_RULES, type BacktestRuleSet } from 'hooks/useBacktest';
import type { AnalysisAuditDetail, AnalysisAuditSummary } from 'types/history';
import type { AnalysisResultItem } from 'types/analysis';
import { fmtDateTime } from './format';
import { downloadZip } from 'utils/download';
import { analysisNo, buildTraceBundle } from 'utils/traceBundle';
import './historyView.css';
import './backtest.css';

type View = 'data' | 'graphic' | 'backtest' | 'fork';

interface HistoryDetailModalProps {
  row: AnalysisAuditSummary;
  /** The pair the History view is showing, so the drill-down opens on the same
   *  rules the verdict column was computed under. */
  windowPair?: BacktestRuleSet;
  onClose: () => void;
}

/** Loads and displays the full input/output for a single persisted analysis. */
export function HistoryDetailModal({ row, windowPair, onClose }: HistoryDetailModalProps) {
  const [detail, setDetail] = useState<AnalysisAuditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('graphic');
  const [btPair, setBtPair] = useState<BacktestRuleSet>(windowPair ?? DEFAULT_RULES);
  const abortRef = useRef<AbortController | null>(null);

  // Which analysis the modal is showing. Usually `row`, but the Fork tab can
  // navigate into a fork — and a fork is typically NOT in the table behind this
  // modal, since listings exclude forks by default. So the trail is kept here
  // rather than pushed back up to HistoryView, which could not resolve the id.
  const [trail, setTrail] = useState<string[]>([row.id]);
  const viewingId = trail[trail.length - 1];
  const isRoot = trail.length === 1;

  // Reset when the modal is pointed at a different row entirely.
  useEffect(() => setTrail([row.id]), [row.id]);

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setDetail(null);
    setError(null);
    getAnalysis(viewingId, ctrl.signal)
      .then((d) => {
        if (!ctrl.signal.aborted) setDetail(d);
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(
          e instanceof ApiError ? `${e.message} (${e.status})` : e instanceof Error ? e.message : 'Failed to load analysis',
        );
      });
    return () => ctrl.abort();
  }, [viewingId]);

  const openAnalysis = (id: string) => {
    setTrail((t) => [...t, id]);
    setView('graphic');
  };
  const goBack = () => setTrail((t) => (t.length > 1 ? t.slice(0, -1) : t));

  const [zipping, setZipping] = useState(false);
  const downloadAll = async () => {
    if (!detail || zipping) return;
    setZipping(true);
    try {
      const { base, files } = await buildTraceBundle(detail);
      await downloadZip(`${base}.zip`, files);
    } catch (e) {
      setError(e instanceof Error ? `Download failed: ${e.message}` : 'Download failed');
    } finally {
      setZipping(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // After navigating into a fork, `row` is the analysis we arrived from — the
  // loaded detail is the one on screen. Header fields read from whichever is
  // current, or they would describe the wrong run.
  const shown = detail ?? (isRoot ? row : null);
  const tag = shown ? `${shown.asset}_${shown.timeframe}_${shown.exchange}` : '';
  const isError = shown?.status === 'error';

  // Reconstruct the AnalysisResultItem the graphic view expects from the stored
  // output + input trace.
  const resultItem: AnalysisResultItem | null =
    detail && detail.output
      ? {
          filename: tag,
          response: detail.output,
          total_tokens: detail.total_tokens,
          debug: detail.input,
        }
      : null;

  return createPortal(
    <div className="hist-overlay" onMouseDown={onClose}>
      <div className="hist-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hist-modal-header">
          <div className="hist-modal-title">
            <span className="hist-modal-main">
              {!isRoot && (
                <button className="hist-back" onClick={goBack} title="Back to the run you came from">
                  ‹
                </button>
              )}
              {shown ? `${shown.seq != null ? `${analysisNo(shown.seq)} · ` : ''}${shown.asset} · ${shown.timeframe} · ${shown.exchange}` : 'Loading…'}
            </span>
            {shown && (
              <span className="hist-modal-sub">
                {engineLabel(shown.engine)} · {shown.username ?? '—'} ·{' '}
                {/* For a fork, created_at is when it was replayed; as_of is the moment
                    the data describes and what the backtest scores it against. Show the
                    latter, or the row reads as an analysis of today's market. */}
                {fmtDateTime(shown.as_of ?? shown.created_at)}
                {shown.is_fork && (
                  <span className="hist-fork-tag" title="Replay of another run's input">
                    fork
                  </span>
                )}
                {isError && <span className="hist-status hist-status-error"> · error</span>}
              </span>
            )}
          </div>
          {!isError && (
            <div className="hist-modal-tabs">
              <button
                className={`hist-tab ${view === 'graphic' ? 'active' : ''}`}
                onClick={() => setView('graphic')}
              >
                Graphic
              </button>
              <button
                className={`hist-tab ${view === 'data' ? 'active' : ''}`}
                onClick={() => setView('data')}
              >
                Data
              </button>
              <button
                className={`hist-tab ${view === 'backtest' ? 'active' : ''}`}
                onClick={() => setView('backtest')}
              >
                Backtest
              </button>
              <button
                className={`hist-tab ${view === 'fork' ? 'active' : ''}`}
                onClick={() => setView('fork')}
                title="Replay this run's stored input through another engine"
              >
                Fork
              </button>
            </div>
          )}
          <button
            className="hist-download"
            onClick={downloadAll}
            disabled={!detail || zipping}
            title="Download every pipeline step as one .zip (one JSON file per step + meta)"
          >
            {zipping ? 'Zipping…' : '↓ All (.zip)'}
          </button>
          <button className="hist-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="hist-modal-body">
          {error && <div className="hist-detail-error">{error}</div>}

          {!error && isError && (
            <div className="hist-detail-error">
              <div className="hist-detail-error-title">Analysis failed</div>
              <pre className="hist-detail-error-pre">{detail?.error ?? '(loading…)'}</pre>
            </div>
          )}

          {!error && !isError && !detail && <div className="hist-loading">Loading…</div>}

          {!error && !isError && detail && (
            <>
              {view === 'graphic' &&
                (resultItem ? (
                  <AnalysisView result={resultItem} />
                ) : (
                  <div className="hist-loading">No output recorded for this run.</div>
                ))}
              {view === 'data' &&
                (detail.input ? (
                  <AnalysisTrace trace={detail.input} tag={tag} engine={detail.engine} />
                ) : (
                  <div className="hist-loading">No input trace recorded for this run.</div>
                ))}
              {view === 'backtest' && (
                <BacktestPanel analysisId={viewingId} pair={btPair} onPairChange={setBtPair} />
              )}
              {view === 'fork' && (
                <ForkPanel
                  row={detail}
                  // The same rules the Backtest tab and the History verdict column
                  // use — outcomes are stored per rule set, so a default here would
                  // silently show verdicts from a sweep nobody is looking at.
                  rules={btPair}
                  onOpenAnalysis={openAnalysis}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
